import { createSelector } from '@reduxjs/toolkit'

import { DefectAssistantSuggestion, OptionValue } from '../actions/defectAssistant'
import type { RootState } from '../index'
import {
  CreateDefectAssistantState,
  DefectCodeAdviceEntry,
  SuggestionRound,
} from './createDefectAssistantSlice'

export type SuggestionDisplayState = 'active' | 'selected' | 'secondary'
export type ExtrasDisplayState = 'active' | 'secondary' | 'dimmed'
export type InterludeMode = 'input' | 'loading' | 'locked'

export type SuggestionAdvice =
  | { kind: 'text'; text: string }
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'none' }

export interface SuggestionVM {
  suggestion: DefectAssistantSuggestion
  label: string
  state: SuggestionDisplayState
  clickable: boolean
  advice: SuggestionAdvice
}

export interface RoundVM {
  key: string
  roundIdx: number
  empty: boolean
  showHeader: boolean
  headerMuted: boolean
  suggestions: SuggestionVM[]
  metaOptions: OptionValue[]
  showFallbackExclude: boolean
  extrasState: ExtrasDisplayState
  extrasEnabled: boolean
  interlude: { mode: InterludeMode; detail: string } | null
  /** Marks the latest submitted-detail round as the post-analysis scroll target. */
  isScrollAnchor: boolean
  /** Whether an empty retry round should offer an escape to the manual form. */
  showRetryEscape: boolean
}

const adviceFor = (
  adviceMap: Record<string, DefectCodeAdviceEntry> | undefined,
  suggestion: DefectAssistantSuggestion
): SuggestionAdvice => {
  if (!adviceMap) {
    return suggestion.description
      ? { kind: 'text', text: suggestion.description }
      : { kind: 'none' }
  }
  const entry = adviceMap[suggestion.defectCode]
  if (!entry || entry.status === 'loading') {
    return { kind: 'loading' }
  }
  return entry.status === 'ready' ? { kind: 'text', text: entry.text } : { kind: 'failed' }
}

const createSuggestionDisplayState = (
  flowStep: CreateDefectAssistantState['flow']['step'],
  pickedRound: boolean,
  suggestionDefectCode: string,
  selectedDefectCode: string
): SuggestionDisplayState => {
  if (flowStep !== 'questioning') {
    return 'active'
  }
  if (pickedRound && suggestionDefectCode === selectedDefectCode) {
    return 'selected'
  }
  return 'secondary'
}

const createInterlude = (
  round: SuggestionRound,
  pickedRound: boolean,
  last: boolean,
  flowStep: CreateDefectAssistantState['flow']['step'],
  empty: boolean
): RoundVM['interlude'] => {
  if (pickedRound) {
    return null
  }
  if (round.detail !== null) {
    return {
      mode: last && flowStep === 'analyzing' ? 'loading' : 'locked',
      detail: round.detail,
    }
  }
  if (last && (flowStep === 'detailing' || (flowStep === 'choosing' && empty))) {
    return { mode: 'input', detail: '' }
  }
  return null
}

export const selectSuggestionRoundsVM = createSelector(
  (state: RootState) => state.createDefectAssistant,
  (s): RoundVM[] => {
    const { rounds, pickedRoundIdx, flow, defectCodeAdviceByGeneration, body } = s

    const extrasEnabled = flow.step !== 'analyzing' && flow.step !== 'detailing'
    const extrasState: ExtrasDisplayState = !extrasEnabled
      ? 'dimmed'
      : flow.step === 'questioning'
      ? 'secondary'
      : 'active'
    const firstSuggestionRoundIdx = rounds.findIndex((round) => round.suggestions.length > 0)
    const lastDetailIdx = rounds.reduce(
      (acc, round, idx) => (round.detail !== null ? idx : acc),
      -1
    )

    return rounds.map((round, i) => {
      const last = i === rounds.length - 1
      const pickedRound = pickedRoundIdx === i
      const empty = round.suggestions.length === 0
      const adviceMap = round.generationId
        ? defectCodeAdviceByGeneration[round.generationId]
        : undefined

      const suggestions: SuggestionVM[] = round.suggestions.map((suggestion) => {
        const state = createSuggestionDisplayState(
          flow.step,
          pickedRound,
          suggestion.defectCode,
          body.defectCode
        )
        return {
          suggestion,
          label: suggestion.category
            ? `${suggestion.category} ➜ ${suggestion.label || suggestion.defectCode}`
            : suggestion.label || suggestion.defectCode,
          state,
          clickable: state !== 'selected',
          advice: adviceFor(adviceMap, suggestion),
        }
      })

      const interlude = createInterlude(round, pickedRound, last, flow.step, empty)

      return {
        key: round.generationId ?? `round-${i}`,
        roundIdx: i,
        empty,
        showHeader: i === firstSuggestionRoundIdx,
        headerMuted: !last || flow.step === 'questioning',
        suggestions,
        metaOptions: round.metaOptions,
        showFallbackExclude: round.metaOptions.length === 0 && round.suggestions.length > 0,
        extrasState,
        extrasEnabled,
        interlude,
        isScrollAnchor: i === lastDetailIdx,
        // This should be removed when it is implemented in mylly AI
        showRetryEscape:
          empty && last && i > 0 && (flow.step === 'choosing' || flow.step === 'detailing'),
      }
    })
  }
)
